
import React, { useState } from "react";
import {
  Box,
  Container,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Link as ChakraLink,
  Stack,
  Text,
  Divider,
    Image,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FaLinkedin, FaFacebook } from "react-icons/fa";
import { scrollPageToTop } from "../../utils/scrollPageToTop";
import ReactCountryFlag from "react-country-flag";
import Logo from "../../assets/images/log.png";
import NotifyModal from "../Modals/NotifyModal";

const FOOTER_BG = "#062238";
const FOOTER_ACCENT = "#14b1a0";
const RADIUS = "5px";

export interface PublicFooterProps {
  /**
   * Dark is the default.
   * Light is suitable for the DashboardLayout.
   */
  variant?: "dark" | "light";

  /**
   * Optional custom background.
   * Overrides the selected variant's background.
   */
  background?: string;
}

interface FooterLink {
  label: string;
  to: string;
}

interface FooterColors {
  background: string;
  text: string;
  heading: string;
  muted: string;
  border: string;
  socialLinkedInBg: string;
  socialLinkedInBorder: string;
  socialFacebookBg: string;
  socialFacebookBorder: string;
  contactBoxBg: string;
  contactBoxBorder: string;
  contactBoxText: string;
}

const companyLinks: FooterLink[] = [
  { label: "Careers", to: "/careers" },
  { label: "Login", to: "/login" },
];

const legalLinks: FooterLink[] = [
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy" },
];

export const getFooterColors = (
  variant: "dark" | "light",
  background?: string
): FooterColors => {
  if (variant === "light") {
    return {
      background: background || "#ffffff",
      text: "#526576",
      heading: "#30333a",
      muted: "#64748b",
      border: "#dce5e8",
      socialLinkedInBg: "rgba(0,119,181,0.08)",
      socialLinkedInBorder: "rgba(0,119,181,0.25)",
      socialFacebookBg: "rgba(20,93,191,0.08)",
      socialFacebookBorder: "rgba(20,93,191,0.25)",
      contactBoxBg: "rgba(20,177,160,0.07)",
      contactBoxBorder: "rgba(20,177,160,0.25)",
      contactBoxText: "#10897e",
    };
  }

  return {
    background: background || FOOTER_BG,
    text: "#c6d4df",
    heading: "#ffffff",
    muted: "#91adbf",
    border: "rgba(255,255,255,0.13)",
    socialLinkedInBg: "rgba(0,119,181,0.14)",
    socialLinkedInBorder: "rgba(0,119,181,0.35)",
    socialFacebookBg: "rgba(20,93,191,0.14)",
    socialFacebookBorder: "rgba(20,93,191,0.35)",
    contactBoxBg: "rgba(20,177,160,0.10)",
    contactBoxBorder: "rgba(20,177,160,0.25)",
    contactBoxText: "#b4e7df",
  };
};

interface FooterNavigationLinkProps extends FooterLink {
  colors: FooterColors;
}


const FooterNavigationLink: React.FC<
  FooterNavigationLinkProps
> = ({ label, to, colors }) => (
  <ChakraLink
    as={RouterLink}
    to={to}
    onClick={scrollPageToTop}
    color={colors.text}
    fontSize="sm"
    textDecoration="none"
    transition="color 0.2s ease"
    _hover={{
      color: colors.heading,
      textDecoration: "none",
    }}
    _focusVisible={{
      outline: `2px solid ${FOOTER_ACCENT}`,
      outlineOffset: "4px",
      borderRadius: RADIUS,
    }}
  >
    {label}
  </ChakraLink>
);


interface FooterHeadingProps {
  children: React.ReactNode;
  colors: FooterColors;
}

const FooterHeading: React.FC<FooterHeadingProps> = ({
  children,
  colors,
}) => (
  <Heading
    as="h3"
    fontSize="sm"
    fontWeight="700"
    color={colors.heading}
    mb={4}
    letterSpacing="0.2px"
  >
    {children}
  </Heading>
);

export default function PublicFooter({
  variant = "dark",
  background,
}: PublicFooterProps) {
  const year = new Date().getFullYear();
  const [contactSubject, setContactSubject] = useState<string | null>(null);

  const colors = getFooterColors(variant, background);

  return (
    <Box
      as="footer"
      role="contentinfo"
      bg={colors.background}
      color={colors.text}
      w="100%"
      borderTop={`1px solid ${FOOTER_ACCENT}`}
    >
      <Container
        maxW="100%"
        px={{ base: 5, md: 8, xl: 12 }}
        pt={{ base: 10, md: 14 }}
        pb={5}
      >
 <Grid
  templateColumns={{
    base: "repeat(2, minmax(0, 1fr))",
    lg: "2fr repeat(4, minmax(0, 1fr))",
  }}
  columnGap={{ base: 4, md: 7, xl: 10 }}
  rowGap={{ base: 7, md: 8 }}
  alignItems="start"
  mb={{ base: 8, md: 12 }}
>
          {/* COLUMN 1 - BRAND */}
          <Stack spacing={4} minW={0}>
           
<Flex
  align="center"
  justify="flex-start"
  gap="8px"
  width="fit-content"
>
  <Image
    src={Logo}
    h="24px"
    w="auto"
    objectFit="contain"
    draggable={false}
    pointerEvents="none"
    flexShrink={0}
  />

  <Heading
    as="h2"
    fontSize="2xl"
    fontWeight="800"
    color={colors.heading}
    letterSpacing="-0.6px"
    lineHeight="1"
    whiteSpace="nowrap"
    m={0}
  >
    Pick
    <Text as="span" color={FOOTER_ACCENT}>
      Dengu
    </Text>
  </Heading>
</Flex>

            <Text
              fontSize="sm"
              lineHeight="1.9"
              color={colors.text}
              maxW="330px"
            >
              Your convenient destination for shopping,
              deliveries, accommodation, car hire and
              travel experiences. Discover services, make
              bookings and receive updates with ease.
            </Text>

            {/* SOCIAL MEDIA ICONS */}
            <HStack spacing={2} align="center">
              {/* LINKEDIN */}
              <ChakraLink
                href="https://www.linkedin.com/company/pickdengu/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PickDengu on LinkedIn"
                title="LinkedIn"
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="36px"
                h="36px"
                borderRadius={RADIUS}
                bg={colors.socialLinkedInBg}
                border={`1px solid ${colors.socialLinkedInBorder}`}
                transition="all 0.2s ease"
                _hover={{
                  bg: "rgba(0,119,181,0.25)",
                  borderColor: "#0077B5",
                  transform: "translateY(-2px)",
                }}
                _focusVisible={{
                  outline: `2px solid ${FOOTER_ACCENT}`,
                  outlineOffset: "3px",
                }}
              >
                <Icon
                  as={FaLinkedin as React.ElementType}
                  boxSize={4}
                  color={
                    variant === "light"
                      ? "#0077B5"
                      : "#49a8dc"
                  }
                />
              </ChakraLink>

              {/* FACEBOOK */}
              <ChakraLink
                href="https://www.facebook.com/lomeditechies.co.za"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PickDengu on Facebook"
                title="Facebook"
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="36px"
                h="36px"
                borderRadius={RADIUS}
                bg={colors.socialFacebookBg}
                border={`1px solid ${colors.socialFacebookBorder}`}
                transition="all 0.2s ease"
                _hover={{
                  bg: "rgba(20,93,191,0.25)",
                  borderColor: "#145DBF",
                  transform: "translateY(-2px)",
                }}
                _focusVisible={{
                  outline: `2px solid ${FOOTER_ACCENT}`,
                  outlineOffset: "3px",
                }}
              >
                <Icon
                  as={FaFacebook as React.ElementType}
                  boxSize={4}
                  color={
                    variant === "light"
                      ? "#145DBF"
                      : "#6ea5f5"
                  }
                />
              </ChakraLink>
            </HStack>
          </Stack>

          {/* COLUMN 2 - SERVICES */}
          <Stack spacing={2.5} align="start">
            <FooterHeading colors={colors}>
              Our Services
            </FooterHeading>

            <FooterNavigationLink label="Marketplace & Shopping" to="/shop" colors={colors} />
            <FooterNavigationLink label="Pickups & Deliveries" to="/delivery/request" colors={colors} />
            <FooterNavigationLink label="Accommodation" to="/book/accommodation" colors={colors} />
            <FooterNavigationLink label="Car Hire" to="/hire/cars" colors={colors} />
            <FooterNavigationLink label="Trips & Experiences" to="/hike" colors={colors} />
          </Stack>

          {/* COLUMN 3 - COMPANY */}
          <Stack spacing={2.5} align="start">
            <FooterHeading colors={colors}>
              Company
            </FooterHeading>

            {companyLinks.map((link) => (
              <FooterNavigationLink
                key={link.to}
                {...link}
                colors={colors}
              />
            ))}

            <Text fontSize="sm">
              Built for convenience
            </Text>

            <HStack spacing={2} align="center">
  <Text fontSize="sm" color={colors.text}>
    Made for Malawi
  </Text>

  <ReactCountryFlag
    countryCode="MW"
    svg
    title="Malawi national flag"
    style={{
      width: "26px",
      height: "17px",
      objectFit: "cover",
      borderRadius: "2px",
      flexShrink: 0,
    }}
  />
</HStack>
          </Stack>

          {/* COLUMN 4 - HELP AND LEGAL */}
          <Stack spacing={2.5} align="start">
            <FooterHeading colors={colors}>
              Help & Legal
            </FooterHeading>

            {legalLinks.map((link) => (
              <FooterNavigationLink
                key={link.to}
                {...link}
                colors={colors}
              />
            ))}

            <ChakraLink
              as="button"
              type="button"
              onClick={() => setContactSubject("Customer Support")}
              color={colors.text}
              fontSize="sm"
              _hover={{
                color: colors.heading,
                textDecoration: "none",
              }}
            >
              Customer Support
            </ChakraLink>

            <ChakraLink
              as="button"
              type="button"
              onClick={() => setContactSubject("Send Feedback")}
              color={colors.text}
              fontSize="sm"
              _hover={{
                color: colors.heading,
                textDecoration: "none",
              }}
            >
              Send Feedback
            </ChakraLink>
          </Stack>

          {/* COLUMN 5 - CONTACT */}
          <Stack spacing={3} align="start">
            <FooterHeading colors={colors}>
              Get in Touch
            </FooterHeading>

            <Box>
              <Text
                fontSize="xs"
                color={colors.muted}
                fontWeight="600"
                mb={1}
              >
                EMAIL
              </Text>

              <ChakraLink
                href="mailto:support@pickdengu.mw"
                fontSize="sm"
                color={colors.heading}
                overflowWrap="anywhere"
                _hover={{ color: FOOTER_ACCENT }}
              >
                support@pickdengu.mw
              </ChakraLink>
            </Box>

            <Box>
              <Text
                fontSize="xs"
                color={colors.muted}
                fontWeight="600"
                mb={1}
              >
                WEBSITE
              </Text>

              <ChakraLink
                href="https://pickdengu.mw"
                target="_blank"
                rel="noopener noreferrer"
                fontSize="sm"
                color={colors.heading}
                _hover={{ color: FOOTER_ACCENT }}
              >
                www.pickdengu.mw
              </ChakraLink>
            </Box>

            <Box>
              <Text
                fontSize="xs"
                color={colors.muted}
                fontWeight="600"
                mb={1}
              >
                LOCATION
              </Text>

              <Text
                fontSize="sm"
                color={colors.heading}
              >
                Malawi
              </Text>
            </Box>

            <Box
              bg={colors.contactBoxBg}
              border={`1px solid ${colors.contactBoxBorder}`}
              borderRadius={RADIUS}
              px={3}
              py={2}
            >
              <Text
                fontSize="xs"
                fontWeight="600"
                color={colors.contactBoxText}
              >
                One shop. Infinite possibilities.
              </Text>
            </Box>
          </Stack>
        </Grid>

        <Divider borderColor={colors.border} />

        {/* COPYRIGHT AND BOTTOM LINKS */}
        <Flex
          pt={5}
          gap={4}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
        >
          <Text
            fontSize="xs"
            color={colors.muted}
            lineHeight="1.8"
          >
            © {year} PickDengu. All rights reserved.
            
          </Text>

          <HStack
            spacing={{ base: 3, md: 5 }}
            flexWrap="wrap"
          >
            <FooterNavigationLink
              label="Careers"
              to="/careers"
              colors={colors}
            />

            <FooterNavigationLink
              label="Terms"
              to="/terms"
              colors={colors}
            />

            <FooterNavigationLink
              label="Privacy"
              to="/privacy"
              colors={colors}
            />
          </HStack>
        </Flex>
      </Container>
      <NotifyModal
        isOpen={contactSubject !== null}
        onClose={() => setContactSubject(null)}
        subject={contactSubject || "Ask Us Anything"}
      />
    </Box>
  );
}
